// src/components/ADHDQuiz.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { ChevronRight, ChevronLeft } from "lucide-react";
import SEO from "./SEO";

const SCALE_OPTIONS = [
  { label_ar: "أبداً",    label_en: "Never",      value: 0 },
  { label_ar: "نادراً",   label_en: "Rarely",     value: 1 },
  { label_ar: "أحياناً",  label_en: "Sometimes",  value: 2 },
  { label_ar: "غالباً",   label_en: "Often",      value: 3 },
  { label_ar: "دائماً",   label_en: "Very Often", value: 4 }
];

// المرجع: DSM-5 (Diagnostic and Statistical Manual of Mental Disorders, 5th Edition)
// American Psychiatric Association, 2013
// معايير تشخيص ADHD المعتمدة دولياً — مُكيَّفة للاستخدام مع أولياء الأمور
const QUIZ_REFERENCE = {
  ar: "المرجع: الدليل التشخيصي والإحصائي للاضطرابات النفسية — الإصدار الخامس (DSM-5)، الجمعية الأمريكية للطب النفسي، 2013",
  en: "Reference: Diagnostic and Statistical Manual of Mental Disorders, 5th Edition (DSM-5), American Psychiatric Association, 2013"
};

const QUIZ_QUESTIONS = [
  {
    id: 1,
    ar: "هل يواجه الطفل صعوبة واضحة في التركيز على الواجبات أو الأنشطة لفترة كافية مقارنة بأقرانه؟",
    en: "Does your child have noticeable difficulty focusing on tasks or activities for a sufficient period compared to peers?"
  },
  {
    id: 2,
    ar: "هل يرتكب الطفل أخطاء بسبب الإهمال في الواجبات أو الاختبارات رغم فهمه للمادة؟",
    en: "Does your child make careless mistakes in schoolwork or tests despite understanding the material?"
  },
  {
    id: 3,
    ar: "هل يبدو الطفل وكأنه لا يستمع عندما يُتحدَّث إليه مباشرة، أو يسرح كثيراً؟",
    en: "Does your child seem not to listen when spoken to directly, or frequently daydream?"
  },
  {
    id: 4,
    ar: "هل يجد صعوبة في متابعة التعليمات أو إكمال الواجبات والأعمال اليومية (إلا إذا كانت ممتعة جداً له)؟",
    en: "Does your child have difficulty following instructions or completing homework and daily tasks (unless they are highly enjoyable)?"
  },
  {
    id: 5,
    ar: "هل ينسى كثيراً إحضار أدواته المدرسية أو أغراضه الشخصية، أو يضيعها بشكل متكرر؟",
    en: "Does your child frequently forget to bring school supplies or personal items, or lose them repeatedly?"
  },
  {
    id: 6,
    ar: "هل يتململ كثيراً (تحريك اليدين والقدمين، هز الكرسي) أو يجد صعوبة في الجلوس بهدوء في الفصل أو البيت؟",
    en: "Does your child fidget frequently (moving hands and feet, rocking the chair) or have difficulty sitting still at school or home?"
  },
  {
    id: 7,
    ar: "هل يترك مقعده في الصف أو في المواقف التي يُنتظر منه فيها الجلوس لفترة؟",
    en: "Does your child leave their seat in the classroom or in situations where remaining seated is expected?"
  },
  {
    id: 8,
    ar: "هل يجري أو يتسلق في أماكن غير مناسبة، أو يظهر عليه شعور داخلي قوي بعدم الهدوء؟",
    en: "Does your child run or climb in inappropriate situations, or show a strong inner feeling of restlessness?"
  },
  {
    id: 9,
    ar: "هل يتحدث الطفل كثيراً، أو يقاطع الآخرين، أو يجيب قبل الانتهاء من طرح السؤال؟",
    en: "Does your child talk excessively, interrupt others, or answer before a question is finished?"
  },
  {
    id: 10,
    ar: "هل يجد صعوبة في انتظار دوره (في الألعاب، أو الإجابة، أو الطابور)؟",
    en: "Does your child have difficulty waiting for their turn (in games, answering, or lines)?"
  },
  {
    id: 11,
    ar: "هل يتشتت انتباهه بسهولة بالمثيرات المحيطة (أصوات، حركة بسيطة، الهاتف، التلفاز)؟",
    en: "Is your child easily distracted by surrounding stimuli (sounds, slight movement, phone, TV)?"
  },
  {
    id: 12,
    ar: "هل تؤدي هذه السلوكيات إلى مشكلات واضحة في الأداء الدراسي أو العلاقات مع الزملاء أو في البيت؟",
    en: "Do these behaviors cause noticeable problems in academic performance, peer relationships, or at home?"
  },
  {
    id: 13,
    ar: "هل يواجه الطفل صعوبة في تنظيم وإنجاز المهام أو الأنشطة (مثل ترتيب خطوات الواجب، تنظيم حقيبته أو أشيائه، أو الالتزام بخطة لإنهاء العمل) مقارنةً بالأطفال في نفس عمره؟",
    en: "Does your child have difficulty organizing and completing tasks or activities (such as arranging homework steps, organizing their backpack or belongings, or adhering to a plan to finish work) compared to children of the same age?"
  }
];
  
export default function ADHDQuiz() {
  const { lang, t } = useLanguage();
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Extra info step (shown after main questions if needed)
  const [childAge, setChildAge] = useState(profile?.childAge || "");
  const [childName, setChildName] = useState(profile?.childName || "");
  const [symptomsHistory, setSymptomsHistory] = useState("");

  const totalSteps = QUIZ_QUESTIONS.length;
  const isExtraStep = step === totalSteps;
  const progress = Math.round(((step) / totalSteps) * 100);

  function selectAnswer(qId, value) {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  }

  function canProceed() {
    if (isExtraStep) return childAge !== "";
    return answers[QUIZ_QUESTIONS[step].id] !== undefined;
  }

  async function handleSubmit() {
    setSubmitting(true);
    const totalScore = Object.values(answers).reduce((a, b) => a + b, 0);
    const maxScore = QUIZ_QUESTIONS.length * 4;
    const percentage = Math.round((totalScore / maxScore) * 100);

    let riskLevel = "low";
    if (percentage >= 60) riskLevel = "high";
    else if (percentage >= 35) riskLevel = "moderate";

    const result = {
      answers,
      totalScore,
      maxScore,
      percentage,
      riskLevel,
      childAge,
      childName,
      symptomsHistory,
      completedAt: serverTimestamp()
    };

    // If logged in, save to Firestore
    if (user) {
      await setDoc(doc(db, "users", user.uid), {
        adhdProfile: result,
        isADHD: riskLevel === "high",
        childAge,
        childName
      }, { merge: true });
    }

    navigate("/quiz/result", { state: { result } });
    setSubmitting(false);
  }

  const currentQ = QUIZ_QUESTIONS[step];

  return (
    <div className="page-content fade-in">
      <SEO 
        title={lang === "ar" ? "اختبار ADHD" : "ADHD Assessment"} 
        description={lang === "ar" ? "أجرِ اختبار تقييم فرط الحركة وتشتت الانتباه (ADHD) المبدئي لطفلك مجاناً وبسرية تامة." : "Take our preliminary ADHD assessment for your child for free and completely confidentially."}
        canonicalUrl="https://www.nodom.site/quiz"
      />
      <div className="container-sm">
        {/* Progress */}
        <div style={{ marginBottom: "2rem" }}>
          <div className="progress-label">
            <span>{lang === "ar" ? `سؤال ${toArabicNum(Math.min(step + 1, totalSteps))} من ${toArabicNum(totalSteps)}` : `Question ${Math.min(step + 1, totalSteps)} of ${totalSteps}`}</span>
            <span>{toArabicNum(progress)}%</span>
          </div>
          <div className="progress-bar-wrap">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="card card-lg">
          {!isExtraStep ? (
            <>
              <div style={{ marginBottom: "2rem" }}>
                <p style={{ fontSize: "0.82rem", color: "var(--color-gray-300)", marginBottom: "0.5rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {lang === "ar" ? `السؤال ${toArabicNum(step + 1)}` : `Question ${step + 1}`}
                </p>
                <h3 style={{ fontSize: "1.2rem", lineHeight: 1.6, color: "var(--color-gray-700)" }}>
                  {lang === "ar" ? currentQ.ar : currentQ.en}
                </h3>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
                {SCALE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    className={`quiz-option ${answers[currentQ.id] === opt.value ? "selected" : ""}`}
                    onClick={() => selectAnswer(currentQ.id, opt.value)}
                  >
                    <span className="quiz-option-dot" />
                    {lang === "ar" ? opt.label_ar : opt.label_en}
                  </button>
                ))}
              </div>
            </>
          ) : (
            /* Extra details step */
            <>
              <h3 style={{ marginBottom: "1.5rem" }}>
                {lang === "ar" ? "معلومات إضافية عن طفلك" : "Additional Information About Your Child"}
              </h3>
              <div className="form-group">
                <label className="form-label">{lang === "ar" ? "اسم الطفل (اختياري)" : "Child's Name (optional)"}</label>
                <input className="form-input" value={childName} onChange={e => setChildName(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">{t("childAgeLabel")} *</label>
                <select className="form-select" value={childAge} onChange={e => setChildAge(e.target.value)} required>
                  <option value="">-- {lang === "ar" ? "اختر العمر" : "Select Age"} --</option>
                  {Array.from({ length: 14 }, (_, i) => i + 3).map(age => (
                    <option key={age} value={age}>
                      {lang === "ar" ? `${toArabicNum(age)} سنوات` : `${age} years`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{lang === "ar" ? "هل سبق تشخيص طفلك؟" : "Has your child been previously diagnosed?"}</label>
                <textarea
                  className="form-textarea"
                  value={symptomsHistory}
                  onChange={e => setSymptomsHistory(e.target.value)}
                  placeholder={lang === "ar" ? "اذكر أي معلومات ذات صلة..." : "Mention any relevant information..."}
                  rows={3}
                />
              </div>
            </>
          )}

          {/* Navigation */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem", gap: "1rem" }}>
            <button
              className="btn btn-outline"
              onClick={() => setStep(s => s - 1)}
              disabled={step === 0}
            >
              {lang === "ar" ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              {t("prev")}
            </button>

            {isExtraStep ? (
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!canProceed() || submitting}
              >
                {submitting ? <><div className="spinner" style={{ width: 16, height: 16, marginLeft: "0.5rem" }} /></> : t("submit")}
              </button>
            ) : step === totalSteps - 1 ? (
              <button
                className="btn btn-primary"
                onClick={() => setStep(totalSteps)}
                disabled={!canProceed()}
              >
                {lang === "ar" ? "التالي" : "Next"}
                {lang === "ar" ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={() => setStep(s => s + 1)}
                disabled={!canProceed()}
              >
                {t("next")}
                {lang === "ar" ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function toArabicNum(n) {
 return n
}
